import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import roleApi from "../../../services/role";
import menuApi from "../../../services/menu";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";

const RoleChartModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [roleData, setRoleData] = useState([]);
  const [menuList, setMenuList] = useState([]);

  const roleChart = useCallback(() => {
    dispatch(showLoader());
    const onSuccess = (res) => {
      if (res?.data?.status) {
        setRoleData(res.data.data);
        dispatch(hideLoader());
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error("Error fetching roles:", err);
    };
    roleApi.getAllRoleChart().then(onSuccess, onFailure);
  }, [dispatch]);

  const getAllMenuList = useCallback(() => {
    const onSuccess = (res) => {
      if (res?.data?.status) {
        setMenuList(res.data.data);
      }
    };
    const onFailure = (err) => {
      console.error("Error fetching menus:", err);
    };
    menuApi.getAllMenu().then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    if (open) {
      roleChart();
      getAllMenuList();
    }
  }, [roleChart, open, getAllMenuList]);

  const getAccessIcon = (accessType) => {
    if (accessType) {
      switch (accessType) {
        case "read_write":
          return (
            <CheckOutlinedIcon
              className="checkIcon"
              style={{ color: "green" }}
            />
          );
        case "read":
          return (
            <CheckOutlinedIcon
              className="readIcon"
              style={{ color: "orange" }}
            />
          );
        case "none":
        default:
          return (
            <CloseOutlinedIcon
              className="noAccessIcon"
              style={{ color: "red" }}
            />
          );
      }
    } else {
      return (
        <CloseOutlinedIcon className="noAccessIcon" style={{ color: "red" }} />
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event) => event.stopPropagation()}
      aria-labelledby="role-chart-modal-title"
      aria-describedby="role-chart-modal-description"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "absolute",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "90%",
          height: "80%",
          maxWidth: "900px",
          backgroundColor: "white",
          boxShadow: 24,
          borderRadius: "8px",
          zIndex: 1300,
          padding: "15px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "5px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <Typography id="role-chart-modal-title" variant="h6" component="h2">
            Role Chart
          </Typography>
          <IconButton onClick={onClose} sx={{ position: "relative" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "auto",
            height: "calc(100% - 84px)",
            padding: "10px",
          }}
        >
          <Table sx={{ marginTop: "16px", minWidth: "1100px" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    textAlign: "center",
                    width: "150px",
                    minWidth: "150px",
                  }}
                >
                  Menu
                </TableCell>
                {roleData.map((role) => (
                  <TableCell key={role.role_id} sx={{ textAlign: "center" }}>
                    {role.role.role.charAt(0).toUpperCase() +
                      role.role.role.slice(1).toLowerCase()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {menuList.map((menu) => (
                <React.Fragment key={menu.id}>
                  <TableRow>
                    <TableCell sx={{ textAlign: "left" }}>
                      {menu.name.charAt(0).toUpperCase() +
                        menu.name.slice(1).toLowerCase()}
                    </TableCell>
                    {roleData.map((role) => {
                      const menuAccess = role.menus.find(
                        (m) => m.id === menu.id
                      );
                      const accessType = menuAccess
                        ? menuAccess.access_type
                        : null;
                      let icon = getAccessIcon(accessType);
                      return (
                        <TableCell
                          key={role.role_id}
                          sx={{ textAlign: "center" }}
                        >
                          {icon}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {menu.submenus && menu.submenus.length > 0
                    ? menu.submenus.map((submenu) => (
                        <TableRow key={submenu.id}>
                          <TableCell sx={{ textAlign: "left" }}>
                            <SubdirectoryArrowRightIcon
                              sx={{ fontSize: "14px" }}
                            />
                            {` ${submenu.name}`}
                          </TableCell>
                          {roleData.map((role) => {
                            const menuAccess = role.menus.find(
                              (m) => m.id === menu.id
                            );
                            const submenuAccess = menuAccess?.submenus?.find(
                              (sm) => sm.id === submenu.id
                            ); // Optional chaining
                            const accessType = submenuAccess
                              ? submenuAccess.access_type
                              : menuAccess
                              ? menuAccess.access_type
                              : null;
                            let submenuIcon = getAccessIcon(accessType);

                            return (
                              <TableCell
                                key={role.role_id}
                                sx={{ textAlign: "center" }}
                              >
                                {submenuIcon}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    : null}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Modal>
  );
};

export default RoleChartModal;
